import * as React from 'react';
import styled from '@emotion/styled';
import {ChangeEvent, Ref} from 'react';

interface RadioProps {
    alias: string;
    ariaLabel?: string;
    caption?: string;
    checked?: boolean;
    className?: string;
    defaultChecked?: boolean;
    disabled?: boolean;
    e2e?: string;
    error?: string;
    errorPosition?: `above` | `below`;
    id?: string;
    inputClass?: string;
    label?: string | JSX.Element;
    labelClass?: string;
    labelGreenText?: string;
    name: string;
    noMargin?: boolean;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    ref?: Ref<unknown> | null;
    subLabel?: string | JSX.Element;
    value?: string;
}

export const RadioStyles = styled.div`
    label {
        &:after {
            background: 50% / 50% 50% no-repeat;
        }
    }
    input:checked ~ label::before {
        color: #fff;
        border-color: #00599c;
        background-color: #00599c;
    }
    input:checked:disabled ~ label::before {
        opacity: 50%;
    }
    input:checked ~ label::after {
        height: 1.5rem;
        width: 1.5rem;
        background-image: url('data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%2712%27 viewBox=%27-4 -4 8 8%27%3e%3ccircle r=%273%27 fill=%27%23fff%27/%3e%3c/svg%3e');
    }
`;

export const RadioComponent = (
    {
        alias,
        ariaLabel,
        caption,
        checked,
        className,
        defaultChecked,
        disabled,
        e2e,
        error = ``,
        errorPosition = `below`,
        id,
        inputClass,
        label,
        labelClass,
        labelGreenText,
        name,
        noMargin = false,
        onChange,
        onFocus,
        subLabel,
        value = ``,
    }: RadioProps,
    ref,
) => {
    return (
        <RadioStyles
            className={`tw-relative tw-z-[1] tw-cursor-pointer hover:tw-cursor-pointer tw-block tw-min-h-[1.5rem] tw-pl-6${
                className ? ' ' + className : ''
            }${noMargin ? ' !tw-mb-0' : ' !tw-mb-2'}`}
            id={name}
        >
            {errorPosition === `above` && error && <label className="tw-text-red-500 tw-ml-2 tw-pl-1">{error}</label>}
            <input
                aria-label={ariaLabel}
                checked={checked}
                className={`tw-absolute tw-left-0 tw-z-[100] tw-w-4 tw-h-5 tw-opacity-0 tw-mt-[0.3rem]${error ? ' tw-border-red-600' : ''}${
                    inputClass ? ' ' + inputClass : ''
                }`}
                defaultChecked={defaultChecked}
                disabled={disabled}
                id={id || `${alias}_radio`}
                name={name}
                onChange={onChange}
                onFocus={onFocus}
                ref={ref}
                type="radio"
                value={value}
                data-e2e={e2e}
            />
            <label
                className={`tw-pt-1.5 tw-pr-0 tw-pb-1 tw-pl-3 tw-relative tw-mb-0 tw-align-top before:tw-w-6 before:tw-h-6 before:tw-rounded-[50%] before:tw-pointer-events-auto before:tw-absolute before:tw-transition-all before:tw-delay-100 before:tw-ease-in-out after:tw-duration-200 before:tw-top-1 before:tw-left-[-1.5rem] before:tw-content-[""] before:tw-border after:tw-absolute after:tw-top-[0.25rem] after:tw-left-[-1.5rem] after:tw-block after:tw-content-[""] after:tw-transition-all after:tw-delay-100 after:tw-ease-in-out after:tw-duration-200${
                    labelClass ? ' ' + labelClass : ''
                }${subLabel ? ' before:tw-top-1/2 before:tw-mt-[-11px] after:!tw-top-1/2 after:tw-mt-[-11px]' : ''}${
                    disabled
                        ? `after:!tw-pointer-events-none !tw-text-gray-400 ${
                              checked
                                  ? 'before:tw-bg-blue-200 before:!tw-opacity-50 before:tw-border-blue-800 before:tw-border-opacity-100'
                                  : 'before:tw-bg-gray-100'
                          }`
                        : ' before:tw-bg-white after:tw-pointer-events-auto'
                }`}
                htmlFor={id || `${alias}_radio`}
            >
                {label ? label : `\u00A0`}
                {labelGreenText ? <span className="tw-text-green-200"> {labelGreenText}</span> : ``}
                {subLabel && <div className="tw-text-sm tw-leading-[1.3] tw-font-normal">{subLabel}</div>}
            </label>
            {errorPosition === `below` && error && <label className="tw-text-red-500 tw-ml-2 tw-pl-1">{error}</label>}
            {caption && <div className="tw-ml-2 tw-pl-1 tw-text-sm tw-leading-[1.3] tw-font-normal">{caption}</div>}
        </RadioStyles>
    );
};
export const Radio = React.forwardRef(RadioComponent);
